<template>
  <BittsCard
    :class="{ 'pl-0 py-4': isStandard }"
    class="partner-population-card p-24"
  >
    <div class="flex items-stretch w-full">
      <div
        v-if="isStandard"
        :class="{
          'opacity-50': population.isEmpty,
        }"
        class="partner-population-card__standard-population-bar"
      />
      <div class="flex flex-col w-full">
        <div
          class="flex flex-col items-start xl:flex-row gap-8 xl:gap-0 justify-between"
        >
          <div
            :style="population.isEmpty ? 'width: 100%' : ''"
            :class="{
              'partner-population-card__standard-name': isStandard,
              'mb-24': !isStandard && hasPopulationToPopulationSettings,
            }"
            class="flex flex-col xl:self-center w-1/2"
          >
            <div class="flex items-center gap-8">
              <component
                :is="isPopulationTooLong ? 'BittsTooltip' : 'div'"
                v-bind="isPopulationTooLong ? tooltipProps : {}"
              >
                <div
                  :class="{ 'opacity-50': population.isEmpty }"
                  class="partner-population-card__population-name partner-population-card__column-population"
                >
                  {{ population.name }}
                </div>
                <template #title>
                  {{ population.name }}
                </template>
              </component>
              <component
                :is="isStandard ? 'BittsTooltip' : 'div'"
                v-bind="isStandard ? tooltipProps : {}"
              >
                <BittsTag
                  :class="{
                    'opacity-50': population.isEmpty,
                  }"
                  :color="isStandard ? 'info' : 'neutral'"
                  variant="rounded"
                  size="x-small"
                  class="pop-type-tag"
                >
                  {{ isStandard ? 'Standard' : 'Custom' }}
                </BittsTag>
                <template #title>
                  Commonly used segments leveraged consistently around
                  Crossbeam. The name cannot be changed
                </template>
              </component>
              <BittsTooltip
                v-if="isSharingGreenfield"
                data-testid="greenfield-sharing-icon-partner"
                placement="topLeft"
                class="partner-population-card__greenfield"
              >
                <FontAwesomeIcon
                  :icon="['fak', 'greenfield']"
                  :style="{
                    height: '12px',
                    width: '12px',
                    color: 'currentColor',
                  }"
                  class="text-success-accent"
                />
                <template #title>
                  {{
                    `${partnerOrg.name} is sharing Greenfield accounts from this Population`
                  }}
                </template>
              </BittsTooltip>
            </div>
            <div
              v-if="population.description"
              :class="{ 'opacity-50': population.isEmpty }"
              class="text-neutral-text text-sm pt-2"
            >
              {{ population.description }}
            </div>
            <div v-else class="text-sm pt-2 text-neutral-text-placeholder">
              {{ noDescriptionText }}
            </div>
          </div>
          <div
            v-if="!population.isEmpty"
            :class="{
              'partner-population-card__standard-column': isStandard,
            }"
            class="partner-population-card__column items-start w-1/3"
          >
            <span class="partner-population-card__column-header xl:mb-5">
              Sharing Setting
            </span>
            <div class="flex items-center" @click.stop>
              <SharingSetting
                :data-share="{
                  ...population.dataShare,
                  sourceFields: population.sourceFields,
                }"
                :is-own="false"
              />
            </div>
          </div>
          <ShareRequestActions
            v-if="canRequestData"
            class="w-[140px] self-start"
            :data-request-information="population.dataRequestInformation"
            @data-requested="openRequestDataModal"
          />
        </div>
        <div
          v-if="hasPopulationToPopulationSettings"
          :class="{ 'ml-16 mb-20': isStandard }"
          class="partner-population-card__pop-to-pop"
        >
          <BittsDivider class="border-neutral-border m-0 p-0 mb-16" />
          <div class="text-neutral-text-weak text-sm mb-4">
            {{ overridesText }}
          </div>
          <div>
            <PopulationToPopulationSharingSetting
              v-for="setting in population.populationToPopulationSettings"
              :key="`pop_to_pop_${setting.dataShare.id}`"
              :setting="setting"
              :has-population-refresh="true"
              direction="incoming"
            />
          </div>
        </div>
      </div>
    </div>
  </BittsCard>
</template>

<script>
import {
  BittsButton,
  BittsCard,
  BittsDivider,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';

import axios from 'axios';
import { mapActions, mapState } from 'pinia';

import PopulationToPopulationSharingSetting from '@/components/partners/PopulationToPopulationSharingSetting.vue';
import ShareRequestActions from '@/components/partners/ShareRequestActions.vue';
import SharingSetting from '@/components/populations/SharingSetting.vue';

import useAuth from '@/composables/useAuth';
import {
  useDataRequestsStore,
  useDataSharesStore,
  useFlashesStore,
  usePartnersStore,
} from '@/stores';
import urls from '@/urls';

const MIN_CHAR_FOR_POP_NAME_OVERFLOW = 34;

export default {
  name: 'PartnerPopulationCard',
  components: {
    BittsButton,
    BittsCard,
    BittsDivider,
    BittsTag,
    BittsTooltip,
    PopulationToPopulationSharingSetting,
    SharingSetting,
    ShareRequestActions,
  },
  props: {
    isStandard: {
      type: Boolean,
      default: false,
    },
    population: {
      type: Object,
      required: true,
    },
  },
  emits: ['show-modal'],
  setup() {
    const { hasScope, hasPermission } = useAuth();

    return { hasScope, hasPermission };
  },
  data() {
    return {
      isOverflowMenuOpen: false,
      requestLoading: false,
    };
  },
  computed: {
    ...mapState(useDataSharesStore, ['greenfieldSharesLookup']),
    hasPopulationToPopulationSettings() {
      return (
        this.population &&
        this.population.populationToPopulationSettings &&
        this.population.populationToPopulationSettings.length > 0
      );
    },
    isPopulationTooLong() {
      return this.population.name.length > MIN_CHAR_FOR_POP_NAME_OVERFLOW;
    },
    isRequestDataSectionVisible() {
      return (
        this.population.requestDataIsVisible &&
        this.hasPermission('manage:sharing')
      );
    },
    isSharingGreenfield() {
      const partnerId = this.population.organization_id;
      return this.greenfieldSharesLookup[partnerId]?.includes(
        this.population.id,
      );
    },
    noDescriptionText() {
      if (!this.population.isEmpty) return 'No description added';
      return 'This partner doesn’t have this Population or isn’t sharing with you. Reach out directly to them to fix this.';
    },
    overridesText() {
      if (this.hasPopulationToPopulationSettings) {
        const overrideNumber =
          this.population.populationToPopulationSettings.length;
        const pluralizedOverride =
          overrideNumber === 1 ? 'Override' : 'Overrides';
        return `${overrideNumber} ${pluralizedOverride}`;
      }
      return '';
    },
    partnerOrg() {
      return (
        this.getPartnerOrgById(this.population.organization_id) || {
          name: 'Partner',
        }
      );
    },
    tooltipProps() {
      return { trigger: 'hover' };
    },
    canRequestData() {
      return !this.population?.isEmpty && this.isRequestDataSectionVisible;
    },
  },
  methods: {
    ...mapActions(usePartnersStore, ['getPartnerOrgById']),
    ...mapActions(useFlashesStore, ['addErrorFlash']),
    ...mapActions(useDataRequestsStore, ['refreshDataRequestsStore']),
    openRequestDataModal() {
      this.$emit('show-modal');
    },
    async cancelShareDataRequest() {
      this.requestLoading = true;
      try {
        const url = urls.shareRequests.outbound.byId(
          this.population.dataRequestInformation.id,
        );
        const payload = {
          status: 'ignored',
        };
        await axios.patch(url, payload);
        await this.refreshDataRequestsStore();
        this.addErrorFlash({
          message: 'Data Request Cancelled',
          description: 'You are no longer requesting data from this population',
        });
      } catch (_err) {
        this.addErrorFlash({
          message: 'Data Request Cancellation Not Successful',
          description:
            'Please try again or contact support@crossbeam.com for help.',
        });
      } finally {
        this.requestLoading = false;
      }
    },
  },
};
</script>

<style lang="pcss" scoped>
.partner-population-card__column {
  @apply flex items-center flex-shrink xl:flex-col;
}

.partner-population-card__column-content {
  @apply text-base text-neutral-900;
}

.partner-population-card__column-header {
  @apply text-sm text-neutral-500 mr-16 xl:mr-0 xl:mb-8;
}

.partner-population-card__column-population {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}

.partner-population-card__population-name {
  @apply text-m font-bold text-neutral-text-strong;
}

.partner-population-card__pop-to-pop {
  @apply flex flex-col text-sm text-neutral-500;
}

.partner-population-card__sharing-setting {
  @apply px-8 py-4 rounded-bts-sm text-brand-navy text-sm;
}

.partner-population-card__standard-column {
  @apply pl-16 xl:pl-0 xl:pt-20;
}

.partner-population-card__standard-name {
  @apply pt-20 xl:pb-20 pl-16;
}

.partner-population-card__standard-population-bar {
  @apply self-stretch w-4 rounded-bts-xl bg-info-accent ml-4;
  flex-shrink: 0;
}
</style>

<style lang="pcss">
.partner-population-card__greenfield {
  .ant-tooltip-inner {
    @apply w-[224px];
  }
}
.pop-type-tag.bitts-tag.bitts-tag__rounded > .ant-tag {
  @apply py-0;
}
</style>
